import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { API_URLS } from '../../Services/servicioURL';

// Interfaces actualizadas
interface Salida {
    folio: string;
    fecha: string;
    observaciones?: string;
    nombre_usuario?: string;
    salidaDetalle: SalidaDetalle[];
}

interface SalidaDetalle {
    id: number;
    cve_producto: string;
    cantidad: number;
    id_sucursal_destino: number;
    cve_departamento_destino: string;
    observaciones: string | null;
    descripcion_producto?: string;
    nombre_sucursal?: string;
    nombre_departamento?: string;
    folio_salida?: string;
    fecha_salida?: string;
    nombre_usuario: string;
    folio?: string;
    fecha?: string;
}

interface Producto {
    cve: string;
    descripcion: string;
    linea?: string;
    sublinea?: string;
    marca?: string;
    unidad?: string;
}

const QR: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    
    const [loading, setLoading] = useState<boolean>(true);
    const [salida, setSalida] = useState<Salida | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [sucursalesMap, setSucursalesMap] = useState<Record<number, string>>({});
    const [departamentosMap, setDepartamentosMap] = useState<Record<string, string>>({});
    const [productosMap, setProductosMap] = useState<Record<string, Producto>>({});
    
    // Estados para el modal de detalles
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [selectedDetalle, setSelectedDetalle] = useState<SalidaDetalle | null>(null);
    
    const folioQuery = searchParams.get('folio') || '';

    const createLocalDate = (dateString: string): Date => {
        if (!dateString) return new Date();
        const dateParts = dateString.split('T')[0].split('-');
        if (dateParts.length === 3) {
            const year = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1;
            const day = parseInt(dateParts[2]);
            return new Date(year, month, day);
        }
        return new Date(dateString);
    };

    const formatLocalDate = (date: Date): string => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return '';
        }
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        const fetchReferenceData = async () => {
            try {
                const sucursalesResponse = await fetch(API_URLS.sucursales.obtener);
                if (sucursalesResponse.ok) {
                    const sucursales = await sucursalesResponse.json();
                    const sucursalesMap = sucursales.reduce((acc: Record<number, string>, sucursal: any) => {
                        acc[sucursal.id] = sucursal.nombre.trim();
                        return acc;
                    }, {});
                    setSucursalesMap(sucursalesMap);
                }

                const departamentosResponse = await fetch(API_URLS.departamentos.obtener);
                if (departamentosResponse.ok) {
                    const departamentos = await departamentosResponse.json();
                    const departamentosMap = departamentos.reduce((acc: Record<string, string>, dept: any) => {
                        acc[dept.cve] = dept.nombre.trim();
                        return acc;
                    }, {});
                    setDepartamentosMap(departamentosMap);
                }

                const productosResponse = await fetch(API_URLS.productos.obtener);
                if (productosResponse.ok) {
                    const productos = await productosResponse.json();
                    const productosMap = productos.reduce((acc: Record<string, Producto>, producto: Producto) => {
                        acc[producto.cve.trim()] = producto;
                        return acc;
                    }, {});
                    setProductosMap(productosMap);
                }
            } catch (error) {
                console.error("Error al cargar datos de referencia:", error);
            }
        };
        fetchReferenceData();
    }, []);

    const buscarSalida = async (folio: string) => {
        if (!folio.trim()) {
            setError('Por favor ingresa un folio de salida válido');
            return;
        }
        setLoading(true);
        setError('');
        setSalida(null);
        
        try {
            const salidasResponse = await fetch(API_URLS.salidas.obtener);
            if (!salidasResponse.ok) throw new Error('Error al obtener salidas');
            const salidas = await salidasResponse.json();
            let salidaEncontrada: Salida | null = null;
            let detallesEncontrados: SalidaDetalle[] = [];

            for (const salida of salidas) {
                if (salida.folio && salida.folio.toString().trim() === folio.trim()) {
                    salidaEncontrada = salida;
                    detallesEncontrados = salida.salidaDetalle && Array.isArray(salida.salidaDetalle) ? salida.salidaDetalle : [];
                    break;
                }
            }

            if (!salidaEncontrada) {
                setError(`No se encontró la salida con folio: ${folio}`);
                setLoading(false);
                return;
            }

            const folioSalida = salidaEncontrada.folio;
            const fechaSalida = salidaEncontrada.fecha;

            const detallesEnriquecidos = detallesEncontrados.map((detalle: SalidaDetalle) => {
                const producto = productosMap[detalle.cve_producto?.toString().trim()];
                return {
                    ...detalle,
                    folio: folioSalida,
                    fecha: fechaSalida,
                    descripcion_producto: producto?.descripcion || 'Producto no encontrado',
                    nombre_sucursal: sucursalesMap[detalle.id_sucursal_destino] || 'Sucursal no encontrado',
                    nombre_departamento: departamentosMap[detalle.cve_departamento_destino?.toString().trim()] || 'Departamento no encontrado',
                    nombre_usuario: detalle.nombre_usuario || ''
                };
            });

            const nombreUsuario = salidaEncontrada.nombre_usuario || (detallesEnriquecidos.length > 0 ? detallesEnriquecidos[0].nombre_usuario : 'Usuario no encontrado');

            setSalida({
                ...salidaEncontrada,
                nombre_usuario: nombreUsuario,
                salidaDetalle: detallesEnriquecidos
            });

        } catch (error) {
            console.error('Error al buscar salida:', error);
            setError('Ocurrió un error al buscar la salida. Por favor intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (folioQuery && Object.keys(sucursalesMap).length > 0 && Object.keys(productosMap).length > 0) {
            setSearchTerm(folioQuery);
            buscarSalida(folioQuery);
        } else if (Object.keys(sucursalesMap).length > 0) {
            setLoading(false);
        }
    }, [folioQuery, sucursalesMap, productosMap, departamentosMap]);

    const handleSearch = () => {
        if (searchTerm.trim()) {
            setSearchParams({ folio: searchTerm.trim() });
            buscarSalida(searchTerm.trim());
        }
    };

    const limpiarBusqueda = () => {
        setSearchTerm('');
        setSearchParams({});
        setSalida(null);
        setError('');
    };

    const showDetalleModal = (detalle: SalidaDetalle) => {
        setSelectedDetalle(detalle);
        setIsModalVisible(true);
    };

    const totalProductos = salida?.salidaDetalle.length || 0;
    const totalCantidad = salida?.salidaDetalle.reduce((total, detalle) => total + detalle.cantidad, 0) || 0;

    return (
        <div style={{
            backgroundImage: 'url(/dist/img/FondoLogin.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            minHeight: '100vh',
            padding: '10px',
            boxSizing: 'border-box'
        }}>
            <Toast ref={toast} />

            {/* Header principal - Completamente responsivo */}
            <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '15px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
                <div style={{ 
                    display: 'flex', 
                    flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                    justifyContent: 'space-between', 
                    alignItems: window.innerWidth <= 768 ? 'stretch' : 'center',
                    gap: '12px'
                }}>
                    <h1 style={{
                        margin: 0,
                        color: '#1f2937',
                        fontSize: window.innerWidth <= 768 ? '1.25rem' : '1.5rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        textAlign: window.innerWidth <= 768 ? 'center' : 'left'
                    }}>
                        📱 Consulta QR
                    </h1>
                    <Button
                        icon="pi pi-arrow-left"
                        label="Volver"
                        size="small"
                        severity="secondary"
                        outlined
                        onClick={() => navigate('/consultor')}
                        style={{ 
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            padding: '8px 12px',
                            width: window.innerWidth <= 768 ? '100%' : 'auto'
                        }}
                    />
                </div>
                <div style={{ 
                    height: '2px', 
                    background: 'linear-gradient(90deg, #ef4444, #f97316)', 
                    borderRadius: '1px',
                    marginTop: '12px'
                }}></div>
            </div>

            {/* Panel de búsqueda - Completamente responsivo */}
            <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '15px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
                <h3 style={{
                    margin: '0 0 12px 0',
                    color: '#374151',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    textAlign: 'center'
                }}>
                    🔍 Buscar Salida
                </h3>
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '10px'
                }}>
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            marginBottom: '5px',
                            color: '#374151'
                        }}>
                            📄 Folio o ID
                        </label>
                        <InputText
                            placeholder="Folio de salida o ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            style={{
                                width: '100%',
                                height: '40px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: '0.9rem'
                            }}
                        />
                        <small style={{ 
                            color: '#6b7280', 
                            fontSize: '0.75rem', 
                            marginTop: '3px', 
                            display: 'block' 
                        }}>
                            💡 Busca por folio o ID
                        </small>
                    </div>
                    <div style={{ 
                        display: 'flex', 
                        gap: '8px'
                    }}>
                        <Button
                            icon="pi pi-search"
                            label="Buscar"
                            onClick={handleSearch}
                            disabled={!searchTerm.trim() || loading}
                            style={{
                                flex: 1,
                                height: '40px',
                                borderRadius: '6px',
                                fontWeight: '600',
                                fontSize: '0.9rem'
                            }}
                        />
                        <Button
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            onClick={limpiarBusqueda}
                            style={{
                                height: '40px',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                padding: '0 12px'
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Mensaje de error */}
            {error && (
                <div style={{ marginBottom: '15px' }}>
                    <Message severity="error" text={error} style={{ width: '100%' }} />
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '10px',
                    padding: '30px',
                    textAlign: 'center',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                    <p style={{ 
                        marginTop: '12px', 
                        color: '#6b7280', 
                        fontSize: '1rem' 
                    }}>
                        🔄 Buscando...
                    </p>
                </div>
            )}

            {/* Información de la salida */}
            {salida && !loading && (
                <>
                    {/* Card de información general */}
                    <Card
                        title={
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '8px'
                            }}>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '8px'
                                }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px',
                                        fontSize: window.innerWidth <= 768 ? '1rem' : '1.1rem'
                                    }}>
                                        📄 Salida
                                        <Badge value={`Folio: ${salida.folio}`} severity="danger" style={{ fontSize: '0.8rem' }} />
                                    </div>
                                    <div style={{ 
                                        display: 'flex', 
                                        gap: '6px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <Badge value={`${totalProductos} productos`} severity="info" />
                                        <Badge value={`${totalCantidad} total`} severity="success" />
                                    </div>
                                </div>
                            </div>
                        }
                        style={{
                            marginBottom: '15px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '10px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: '15px',
                            padding: '12px 0'
                        }}>
                            <div>
                                <strong style={{ 
                                    color: '#374151', 
                                    display: 'block', 
                                    marginBottom: '6px',
                                    fontSize: '0.9rem'
                                }}>
                                    📅 Fecha:
                                </strong>
                                <p style={{
                                    margin: 0,
                                    padding: '10px',
                                    backgroundColor: '#fef2f2',
                                    borderRadius: '6px',
                                    border: '1px solid #fecaca',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    color: '#dc2626'
                                }}>
                                    {formatLocalDate(createLocalDate(salida.fecha))}
                                </p>
                            </div>
                            <div>
                                <strong style={{ 
                                    color: '#374151', 
                                    display: 'block', 
                                    marginBottom: '6px',
                                    fontSize: '0.9rem'
                                }}>
                                    👤 Usuario:
                                </strong>
                                <p style={{
                                    margin: 0,
                                    padding: '10px',
                                    backgroundColor: '#f0f9ff',
                                    borderRadius: '6px',
                                    border: '1px solid #bae6fd',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    color: '#0369a1'
                                }}>
                                    {salida.nombre_usuario || 'Usuario no encontrado'}
                                </p>
                            </div>
                            {salida.observaciones && (
                                <div>
                                    <strong style={{ 
                                        color: '#374151', 
                                        display: 'block', 
                                        marginBottom: '6px',
                                        fontSize: '0.9rem'
                                    }}>
                                        📝 Observaciones:
                                    </strong>
                                    <p style={{
                                        margin: 0,
                                        padding: '10px',
                                        backgroundColor: '#fffbeb',
                                        borderRadius: '6px',
                                        border: '1px solid #fed7aa',
                                        fontSize: '0.95rem',
                                        lineHeight: '1.4',
                                        color: '#92400e'
                                    }}>
                                        {salida.observaciones}
                                    </p>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Lista de productos - Cards individuales para móviles */}
                    <Card
                        title={
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px',
                                fontSize: window.innerWidth <= 768 ? '1rem' : '1.1rem'
                            }}>
                                📦 Productos
                                <Badge value={totalProductos} severity="info" />
                            </div>
                        }
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '10px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            overflow: 'hidden'
                        }}
                    >
                        <div style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            {salida.salidaDetalle.length > 0 ? (
                                salida.salidaDetalle.map((detalle, index) => {
                                    const producto = productosMap[detalle.cve_producto.trim()];
                                    return (
                                        <div
                                            key={index}
                                            onClick={() => showDetalleModal(detalle)}
                                            style={{
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px',
                                                padding: '12px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f8fafc';
                                                e.currentTarget.style.borderColor = '#3b82f6';
                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#ffffff';
                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                            }}
                                        >
                                            {/* Header del producto */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: '8px',
                                                flexWrap: 'wrap',
                                                gap: '8px'
                                            }}>
                                                <div style={{ flex: 1, minWidth: '200px' }}>
                                                    <div style={{
                                                        fontWeight: '600',
                                                        fontSize: '0.95rem',
                                                        color: '#1f2937',
                                                        marginBottom: '4px'
                                                    }}>
                                                        📦 {detalle.cve_producto}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.85rem',
                                                        color: '#6b7280',
                                                        lineHeight: '1.3'
                                                    }}>
                                                        {detalle.descripcion_producto}
                                                    </div>
                                                    {producto && (producto.marca || producto.linea) && (
                                                        <div style={{
                                                            fontSize: '0.75rem',
                                                            color: '#9ca3af',
                                                            marginTop: '2px'
                                                        }}>
                                                            {producto.marca && `${producto.marca} • `}
                                                            {producto.linea}
                                                        </div>
                                                    )}
                                                </div>
                                                <Badge 
                                                    value={detalle.cantidad} 
                                                    severity="info" 
                                                    style={{ 
                                                        fontSize: '0.9rem',
                                                        padding: '4px 8px'
                                                    }} 
                                                />
                                            </div>

                                            {/* Información de destino */}
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
                                                gap: '8px',
                                                marginBottom: '8px'
                                            }}>
                                                <div style={{
                                                    backgroundColor: '#f0f9ff',
                                                    padding: '8px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #bae6fd'
                                                }}>
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        color: '#0369a1',
                                                        fontWeight: '600',
                                                        marginBottom: '2px'
                                                    }}>
                                                        🏢 SUCURSAL
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.85rem',
                                                        color: '#1e40af',
                                                        fontWeight: '500'
                                                    }}>
                                                        {detalle.nombre_sucursal}
                                                    </div>
                                                </div>
                                                <div style={{
                                                    backgroundColor: '#f0fdf4',
                                                    padding: '8px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #bbf7d0'
                                                }}>
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        color: '#059669',
                                                        fontWeight: '600',
                                                        marginBottom: '2px'
                                                    }}>
                                                        🏬 DEPARTAMENTO
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.85rem',
                                                        color: '#047857',
                                                        fontWeight: '500'
                                                    }}>
                                                        {detalle.nombre_departamento}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Usuario y observaciones */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: '8px'
                                            }}>
                                                <div style={{
                                                    fontSize: '0.8rem',
                                                    color: '#0369a1',
                                                    fontWeight: '600',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    👤 {detalle.nombre_usuario}
                                                </div>
                                                {detalle.observaciones && (
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        color: '#6b7280',
                                                        fontStyle: 'italic',
                                                        maxWidth: '150px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        📝 {detalle.observaciones}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Indicador de ID */}
                                            {detalle.id && (
                                                <div style={{
                                                    marginTop: '8px',
                                                    textAlign: 'right'
                                                }}>
                                                    <Badge 
                                                        value={`ID: ${detalle.id}`} 
                                                        severity="secondary" 
                                                        style={{ fontSize: '0.7rem' }} 
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '40px 20px',
                                    color: '#6b7280'
                                }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📭</div>
                                    <p style={{ margin: 0, fontSize: '1rem' }}>
                                        No hay productos en esta salida
                                    </p>
                                </div>
                            )}
                        </div>
                    </Card>
                </>
            )}

            {/* Mensaje cuando no hay salida */}
            {!salida && !loading && !error && (
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '10px',
                    padding: '25px',
                    textAlign: 'center',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📱</div>
                    <h3 style={{ 
                        color: '#374151', 
                        marginBottom: '10px',
                        fontSize: '1.3rem'
                    }}>
                        Consulta QR
                    </h3>
                    <p style={{ 
                        color: '#6b7280', 
                        fontSize: '0.95rem', 
                        lineHeight: '1.5',
                        margin: '0 0 12px 0'
                    }}>
                        Ingresa un folio o ID para ver la información.
                    </p>
                    <p style={{ 
                        color: '#9ca3af', 
                        fontSize: '0.85rem' 
                    }}>
                        💡 Escanea un QR para llegar aquí
                    </p>
                </div>
            )}

            {/* Modal de detalles del producto */}
            <Dialog
                header="📦 Detalles del Producto"
                visible={isModalVisible}
                style={{ 
                    width: window.innerWidth <= 768 ? '95%' : '600px',
                    maxWidth: '95vw'
                }}
                onHide={() => setIsModalVisible(false)}
                breakpoints={{ '960px': '75vw', '641px': '95vw' }}
            >
                {selectedDetalle && (
                    <div style={{ padding: '10px 0' }}>
                        {/* Información del producto */}
                        <div style={{
                            backgroundColor: '#f8fafc',
                            padding: '15px',
                            borderRadius: '8px',
                            marginBottom: '15px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <h4 style={{ 
                                margin: '0 0 10px 0', 
                                color: '#1f2937',
                                fontSize: '1.1rem'
                            }}>
                                📦 {selectedDetalle.cve_producto}
                            </h4>
                            <p style={{
                                margin: '0 0 8px 0',
                                color: '#4b5563',
                                fontSize: '0.95rem',
                                lineHeight: '1.4'
                            }}>
                                {selectedDetalle.descripcion_producto}
                            </p>
                            {productosMap[selectedDetalle.cve_producto.trim()] && (
                                <div style={{
                                    fontSize: '0.85rem',
                                    color: '#6b7280'
                                }}>
                                    {productosMap[selectedDetalle.cve_producto.trim()].marca && 
                                        `Marca: ${productosMap[selectedDetalle.cve_producto.trim()].marca} • `}
                                    {productosMap[selectedDetalle.cve_producto.trim()].linea && 
                                        `Línea: ${productosMap[selectedDetalle.cve_producto.trim()].linea}`}
                                </div>
                            )}
                        </div>

                        {/* Información de la salida */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: '12px'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px',
                                backgroundColor: '#fef2f2',
                                borderRadius: '6px',
                                border: '1px solid #fecaca'
                            }}>
                                <span style={{ fontWeight: '600', color: '#374151' }}>📄 Folio:</span>
                                <Badge value={selectedDetalle.folio} severity="danger" />
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px',
                                backgroundColor: '#f0f9ff',
                                borderRadius: '6px',
                                border: '1px solid #bae6fd'
                            }}>
                                <span style={{ fontWeight: '600', color: '#374151' }}>📊 Cantidad:</span>
                                <Badge value={selectedDetalle.cantidad} severity="info" />
                            </div>

                            <div style={{
                                padding: '10px',
                                backgroundColor: '#f0fdf4',
                                borderRadius: '6px',
                                border: '1px solid #bbf7d0'
                            }}>
                                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '5px' }}>
                                    🏢 Sucursal Destino:
                                </div>
                                <div style={{ color: '#047857', fontSize: '0.95rem' }}>
                                    {selectedDetalle.nombre_sucursal}
                                </div>
                            </div>

                            <div style={{
                                padding: '10px',
                                backgroundColor: '#fffbeb',
                                borderRadius: '6px',
                                border: '1px solid #fed7aa'
                            }}>
                                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '5px' }}>
                                    🏬 Departamento Destino:
                                </div>
                                <div style={{ color: '#92400e', fontSize: '0.95rem' }}>
                                    {selectedDetalle.nombre_departamento}
                                </div>
                            </div>

                            <div style={{
                                padding: '10px',
                                backgroundColor: '#f0f9ff',
                                borderRadius: '6px',
                                border: '1px solid #bae6fd'
                            }}>
                                <div style={{ fontWeight: '600', color: '#374151', marginBottom: '5px' }}>
                                    👤 Usuario:
                                </div>
                                <div style={{ color: '#0369a1', fontSize: '0.95rem' }}>
                                    {selectedDetalle.nombre_usuario}
                                </div>
                            </div>

                            {selectedDetalle.observaciones && (
                                <div style={{
                                    padding: '10px',
                                    backgroundColor: '#fefce8',
                                    borderRadius: '6px',
                                    border: '1px solid #fde047'
                                }}>
                                    <div style={{ fontWeight: '600', color: '#374151', marginBottom: '5px' }}>
                                        📝 Observaciones:
                                    </div>
                                    <div style={{ 
                                        color: '#a16207', 
                                        fontSize: '0.95rem',
                                        lineHeight: '1.4'
                                    }}>
                                        {selectedDetalle.observaciones}
                                    </div>
                                </div>
                            )}

                            {selectedDetalle.id && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '10px'
                                }}>
                                    <Badge 
                                        value={`ID del Detalle: ${selectedDetalle.id}`} 
                                        severity="secondary" 
                                        style={{ fontSize: '0.8rem' }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Estilos CSS adicionales para responsividad */}
            <style>
                {`
                    @media (max-width: 768px) {
                        .content-wrapper {
                            background-attachment: scroll !important;
                        }
                        
                        .p-dialog .p-dialog-content {
                            padding: 1rem !important;
                        }
                        
                        .p-dialog .p-dialog-header {
                            padding: 1rem !important;
                        }
                    }
                    
                    @media (max-width: 480px) {
                        .p-dialog {
                            margin: 0.5rem !important;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default QR;